import dagre from 'dagre';
import produce from 'immer';

import pick from 'util/pick';

import {
  ANALYSIS_FLOWCHART_EDGE_LABEL_ICON_HEIGHT,
  ANALYSIS_FLOWCHART_NODE_WIDTH,
} from '../AnalysisFlowchartStyleConstants';
import {AnalysisGraph, AnalysisGraphEdge, AnalysisGraphNode} from './AnalysisGraphTypes';
import AnalysisGraphUtils from './AnalysisGraphUtils';

/**
 * The minimum amount of space afforded a node in the layout.
 *
 * A node with a two-line title and a two-line status message will consume about this amount of space; nodes
 * with shorter titles or no status messages will be smaller.
 *
 * Include the height for a status message for all nodes, so the flowchart doesn't rearrange as the pipeline
 * executes and statuses change.
 */
const MIN_NODE_HEIGHT = 95;

/**
 * Estimate a node's expected height, for layout purposes.
 */
function calculateNodeHeight(node: AnalysisGraphNode): number {
  const numInputStepDatas = node.data.step.inputStepDatas.length;
  const numOutputStepDatas = node.data.step.outputStepDatas.length;
  // For single-input or single-output steps, inputs/output step datas are not individually listed
  const numRows =
    (numInputStepDatas > 1 ? numInputStepDatas + 1 : 0) +
    (numOutputStepDatas > 1 ? numOutputStepDatas + 1 : 0);
  return MIN_NODE_HEIGHT + numRows * 20;
}

/**
 * Estimate an edge's width, for layout purposes.
 */
function calculateEdgeLabelWidth({data: {edge}}: AnalysisGraphEdge): number {
  // +2 to provide some extra spacing between nodes; otherwise, edges look cramped
  return ANALYSIS_FLOWCHART_EDGE_LABEL_ICON_HEIGHT * (AnalysisGraphUtils.getEdgeRawDatas(edge).length + 2);
}

/**
 * Compute positions to arrange the flowchart graph of an analysis.
 *
 * If `AnalysisFlowchart` is updated to have nodes of different sizes or edges with labels,
 * this algorithm must be complexified.
 *
 * @param unarrangedGraph An AnalysisFlowchart with uncomputed `position` values on the nodes
 * @returns An AnalysisFlowchart with updated `position` values on the nodes, to ensure an attractive layout
 */
export default function arrangeAnalysisGraph(unarrangedGraph: AnalysisGraph): AnalysisGraph {
  const dagreGraph = new dagre.graphlib.Graph();

  dagreGraph.setGraph({rankdir: 'LR'}); // 'LR' = arrange left to right
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  unarrangedGraph.nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      height: calculateNodeHeight(node),
      width: ANALYSIS_FLOWCHART_NODE_WIDTH,
    });
  });
  unarrangedGraph.edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target, {
      height: ANALYSIS_FLOWCHART_EDGE_LABEL_ICON_HEIGHT,
      width: calculateEdgeLabelWidth(edge),
    });
  });

  dagre.layout(dagreGraph);

  return {
    nodes: unarrangedGraph.nodes.map((node) =>
      produce(node, (draftNode) => {
        draftNode.position = pick(dagreGraph.node(node.id), 'x', 'y');
      })
    ),
    edges: unarrangedGraph.edges,
  };
}
